/* ============================================================
   SLATE AI Help Assistant — Knowledge Base
   Roles: 1=Admin, 2=Mentor, 3=StateOfficer
============================================================ */

export const KNOWLEDGE = {
  'add school': {
    title: 'How to Add a School',
    roles: [1, 2, 3],
    steps: [
      'Go to School section in the left sidebar.',
      'Click "Add School" from the dropdown menu.',
      'Fill School Name (max 150 chars) and UDISE Code (must be unique).',
      'Select State and District from the dropdowns.',
      'Enter Pincode and Address.',
      'Optionally fill: Geo Location, School Email ID, Primary Contact Person, Contact No, Total Students.',
      'Click Submit to save the school.',
    ],
    note: 'UDISE code must be unique. All three roles (Admin, Mentor, State Officer) can add schools.',
  },

  'tag lab to school': {
    title: 'How to Tag a Lab to a School',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Tag Lab to School".',
      'Enter Lab Name (required).',
      'Select Lab Type: Atal Tinkering Lab, Aghaaz Lab, SATHEE, or Artificial Intelligence Lab.',
      'Enter Lab Capacity (number of students) and Lab Description (max 500 chars).',
      'Select the School Name from the dropdown.',
      'Click Submit to link the lab to the school.',
    ],
    note: 'A lab must be tagged to a school before sessions can be created. All three roles can tag labs.',
  },

  'add equipment': {
    title: 'How to Add Equipment',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Add Equipment".',
      'Enter Equipment Name and Equipment Description.',
      'Select Warranty Status from the dropdown.',
      'Enter Quantity, Date of Expiry, and Serial Number.',
      'Click Submit to save the equipment.',
    ],
    note: 'After adding equipment, use Equipment Allocation to assign it to a school or lab.',
  },

  'equipment allocation': {
    title: 'How to Allocate Equipment',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Equipment Allocation".',
      'Select one or more School Names from the multi-select list.',
      'Select the Lab Name from the dropdown.',
      'Select one or more Equipment Names from the multi-select list (shows available quantity).',
      'Set the Allocation Date.',
      'The Available Quantity field auto-fills. Enter the Allocation Quantity.',
      'Click Submit to complete the allocation.',
    ],
    note: 'Equipment must be added first before it can be allocated. All three roles can allocate equipment.',
  },

  'session': {
    title: 'How to Create a Session',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Session Setup".',
      '— Section 1: Enter Session Title, select Session Host, Session Date, Session Time (with AM/PM).',
      'Select School Name and Lab Name from dropdowns.',
      'Enter Session Description (max 300 chars).',
      '— Section 2 (SATHEE sessions): Fill Centre Code, State, District/Block/Zone.',
      'Enter Name of Coordinator, select Type of School Visited.',
      'Enter School Address, Principal Name, and Principal/POC Contact.',
      'Select Mode of Visit: In-person, Virtual, or Phone Call.',
      'Upload List of Invitees Excel file (.xls/.xlsx) — Download Template link is available.',
      'Upload Session Images (optional, max 10 images — appear in PDF reports).',
      'Click Submit at the bottom.',
    ],
    note: 'The Invitees Excel file is required. Lab must be tagged to a school before a session can be created.',
  },

  'hcl sathee': {
    title: 'HCL SATHEE Registration & Tracking',
    roles: [1, 2, 3],
    steps: [
      'Go to the HCL SATHEE section in the sidebar.',
      'Click "Registration" to add a new HCL SATHEE student.',
      '— Enter Name, Email, and select School Name.',
      '— Fill in personal details (Gender, Social Group, DOB).',
      '— Enter Contact Information (Mobile, Address, State, District, Pincode).',
      '— Specify Academic Details (Class, Subjects, Medium of Study, Coaching Details).',
      'Click Submit to securely save the student record.',
      'To view registered students, go to the Registered Students list under HCL SATHEE.',
    ],
    note: 'HCL SATHEE registrations are tracked entirely separately from standard SATHEE. Only Admins can see all students. State Officers and Mentors can only see students registered in their own state/lab.',
  },

  'my entries': {
    title: 'How to Use My Entries — All Entries',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "My Entries - All Entries".',
      '— TOGGLE: "My Entities" shows only your own data. "All Entities" shows all data (Admin sees all; Mentor and State Officer see their own).',
      '',
      '📂 SESSIONS TAB',
      '  • Table shows: Session Title, Session Host, Session Date, Setup By, Images, Download, Action (Track).',
      '  • The list strictly sorts chronologically from newest session date to oldest session date!',
      '  • Download button: downloads ALL session images as a ZIP file.',
      '  • Admin: can download images for ALL sessions (All Entities toggle).',
      '  • Mentor: can only download images for their OWN sessions (My Entities view only).',
      '  • State Officer: CANNOT download session images — no Download button visible.',
      '  • Track button: opens session tracking/attendance details.',
      '',
      '📂 SATHEE STUDENTS TAB',
      '  • Table shows: ID, Student Name, School, Mobile Number, Email, Registration Date, Action (Delete).',
      '  • Admin sees all registered students. State Officers see their state\'s students. Mentors see their lab\'s students.',
    ],
    note: 'Session images download as a ZIP file named "SessionTitle_images.zip". State Officers cannot download session images.',
  },

  'sathee registration': {
    title: 'How to Register a standard SATHEE Student',
    roles: [1, 2, 3],
    steps: [
      'Go to School in the sidebar.',
      'Click "Sathee Registration".',
      '— Personal Information: Enter Name, Father Name, Date of Birth, Email ID.',
      'Select Gender, Social Group from dropdowns. Enter Mobile Number (optional/WhatsApp).',
      '— Location: Select Country, State, District, and enter City/Village.',
      '— School & Course: Select Organization Type, School Type, Class.',
      'Enter School Name and Course Opted (optional).',
      '— How Did You Know About SATHEE: Check applicable options.',
      'Accept Terms and Conditions checkbox.',
      'Click Submit to register.',
    ],
    note: 'SATHEE registrations are tracked separately from session attendance. HCL SATHEE has its own dedicated section.',
  },

  'library': {
    title: 'How to Use the Library',
    roles: [1, 2, 3],
    steps: [
      'Click "Library" in the left sidebar.',
      '— Search: Search artifacts by typing keywords or selecting tags.',
      '— My Artifacts: Shows only documents you have uploaded.',
      '— Upload Document: Click Upload Document, select your file, choose Document Type, add Tags, write a Description, toggle Publish, and click Submit.',
      '— Add URL: Click Add URL, enter Title and URL, add Tags, select Document Type, write Description, toggle Publish, and click Submit.',
      '— Manage All Artifacts (Admin only): View and edit/delete any document uploaded by any user.',
    ],
    note: 'File size must be within the upload limit set in System Settings (currently 2500 KB).',
  },

  'reports': {
    title: 'Reports & Analytics — Overview',
    roles: [1, 2, 3],
    steps: [
      'Click "Reports" in the sidebar.',
      'The page has 3 tabs: Dashboard Stats, Download Reports (Excel), and PDF Reports.',
      '— Admin: Full Access · 26 reports available · can filter by State.',
      '— State Officer: Your State Only · 22 reports · state filter is locked to your state.',
      '— Mentor: Your Lab Only · 11 reports · marked "Restricted to your assigned lab".',
    ],
    note: 'Admin sees all states and labs. State Officer sees only their state data. Mentor sees only their assigned lab data.',
  },

  'dashboard stats': {
    title: 'Dashboard Stats — Reports',
    roles: [1, 2, 3],
    steps: [
      'Go to Reports → Dashboard Stats tab.',
      'Summary cards show: Total Sessions, Total Attendees, Active Labs, Active Mentors, Schools.',
      'A Sessions per Month bar chart is shown below.',
      'Tables below the chart: Sessions by Lab, Sessions by Mentor, Sessions by School.',
      '— Admin: Filter by From Date, To Date, and State. Click Apply Filter.',
      '— State Officer: Filter by date only (state is auto-applied).',
      '— Mentor: Filter by date only (lab is auto-applied).',
    ],
    note: 'Admin sees platform-wide data. State Officer and Mentor see scoped data only.',
  },

  'excel reports': {
    title: 'Download Reports — All Excel Report Types',
    roles: [1, 2, 3],
    steps: [
      'Go to Reports → Download Reports tab.',
      'Select a Report Type from the dropdown.',
      'Select From Date and To Date.',
      'Click Download Excel Report.',
    ],
    note: 'Data is automatically scoped based on your role (Admin, State Officer, or Mentor).',
  },

  'pdf reports': {
    title: 'PDF Reports',
    roles: [1, 2, 3],
    steps: [
      'Go to Reports → PDF Reports tab.',
      'Select a Report Type (e.g., Session Detail Report).',
      'Select Session Title.',
      'Click Download PDF.',
    ],
    note: 'The PDF will contain all session details and up to 10 embedded session images.',
  },

  'grievance': {
    title: 'How to Submit a Grievance',
    roles: [1, 2, 3],
    steps: [
      'Click "Grievance" in the sidebar.',
      'Click the "Submit Grievance" tab.',
      'Select an Issue Category (e.g. Hardware, Software, Platform).',
      'Select a Sub-Category.',
      'Enter the Description of the issue (max 500 chars).',
      '— Upload Image: Attach a PNG or JPG image (max 5MB) if available.',
      'Click Submit Grievance button.',
    ],
    note: 'After submission, a unique Grievance ID is assigned. Track its status under the My Grievances tab.',
  },

  'my grievances': {
    title: 'How to View My Grievances',
    roles: [1, 2, 3],
    steps: [
      'Click "Grievance" in the sidebar.',
      'Click the "My Grievances" tab.',
      'You will see all grievances you submitted.',
      'Each row shows: Grievance ID, Status, Category, and Description.',
      'Status flow: Pending → In Progress → Resolved → Closed.',
    ],
    note: 'Admins can view and manage ALL grievances via God Mode → Manage Grievances.',
  },

  'profile': {
    title: 'How to Update My Profile',
    roles: [1, 2, 3],
    steps: [
      'Click "My Profile" in the sidebar.',
      'View your avatar (with initials), Name, Email, Role badge, Scope, and Phone.',
      '— Admin scope shows "All India".',
      'To change password: scroll to Reset Password section.',
      'Enter Old Password, New Password, and Confirm Password.',
      'Click Update Password button.',
    ],
    note: 'Email cannot be changed from the profile page. Contact your Administrator if your email needs to be updated.',
  },

  'god mode': {
    title: 'God Mode — Admin-Only Tools',
    roles: [1],
    steps: [
      'God Mode is visible only to Administrators (shown in yellow in the sidebar).',
      '⚙️ System Settings — Set file upload limit and toggle allowed document formats.',
      '👁️ Check User Activity — Track user activity logs.',
      '👤 User Access — Create, edit, and manage all users and roles.',
      '🔒 RBAC — Toggle sidebar feature access per user.',
      '🧪 Add Lab Type — Add/Edit/Delete lab types.',
      '📄 Define Document Type — Add new document categories for the Library.',
      '🗂️ Manage All Artifacts — View all uploaded documents.',
      '🏷️ Edit Tags — View and edit tags.',
      '🏫 Manage Schools & Labs — View school cards with UDISE, location, contact.',
      '📢 Manage Grievances — Full grievance management with status updates.',
    ],
    note: 'God Mode is ONLY for Administrators. Mentors and State Officers have no access to any God Mode feature.',
  },

  'manage grievances': {
    title: 'How to Manage Grievances (Admin — God Mode)',
    roles: [1],
    steps: [
      'Go to God Mode → Manage Grievances in the sidebar.',
      'Filter by Status or Priority. Search by ID, user email, or category.',
      'Click any grievance row to open the Grievance Details modal.',
      'Select new Status from dropdown: Pending / In Progress / Resolved / Closed.',
      'Add Remarks / Resolution Notes in the textarea.',
      'Click the green Update Status button to save changes.',
    ],
    note: 'Only Administrators can manage all grievances. Priority levels are: Low, Medium, High, Critical.',
  },

  'add user': {
    title: 'How to Add a New User & Data Isolation',
    roles: [1],
    steps: [
      'Go to God Mode → User Access.',
      'Click the Create User button.',
      'Fill: First Name, Last Name, Email, Phone.',
      'Select User Type: Admin, Mentor, or State Officer.',
      'Select the State for the user.',
      'Select Lab Type to Assign (e.g. HCL SATHEE, Atal Tinkering Lab).',
      'Click Submit to create the account.',
      '— Automatic Placeholder Lab: When creating a State Officer or Mentor, the system automatically generates a unique placeholder lab tied specifically to their State to strictly isolate their data views!',
    ],
    note: 'Only Admins can create new user accounts. Mentors and State Officers automatically receive restricted data views through placeholder labs tied to their user records.',
  },

  'rbac': {
    title: 'RBAC — Role Based Access Control',
    roles: [1],
    steps: [
      'Go to God Mode → RBAC.',
      'The table shows all users with their Username, Title (role shown in color).',
      'Toggle columns control access to: Add School, Tag Lab to School, Add Equipment, Equipment Allocation, Session Setup, and Reports.',
      'Toggle ON to grant access, toggle OFF to revoke.',
    ],
    note: 'RBAC gives fine-grained control — e.g. you can give a Mentor access to Reports but block Equipment Allocation.',
  },

  'system settings': {
    title: 'System Settings (God Mode)',
    roles: [1],
    steps: [
      'Go to God Mode → System Settings.',
      'Update File Upload Limit or Total Space Allocated.',
      'Toggle allowed Document Formats For Library (jpg, png, pdf, docx, etc).',
    ],
    note: 'Changes to upload limit and allowed formats affect all users immediately.',
  },

  'check user activity': {
    title: 'Check User Activity (God Mode)',
    roles: [1],
    steps: [
      'Go to God Mode → Check User Activity.',
      'Select a user from the Users dropdown.',
      'Select a Period: Last 30 days, Last 90 days, Last 180 days, or Last 365 days.',
      'Click the Track button to see the logs.',
    ],
    note: 'Useful for auditing user actions and monitoring platform engagement.',
  },

  'getting started': {
    title: 'Getting Started with SLATE',
    roles: [1, 2, 3],
    steps: [
      'Step 1 — Add Schools: School → Add School (all roles).',
      'Step 2 — Tag Labs to Schools: School → Tag Lab to School (all roles).',
      'Step 3 — Add Equipment: School → Add Equipment (all roles).',
      'Step 4 — Allocate Equipment to Labs: School → Equipment Allocation (all roles).',
      'Step 5 — Create Sessions: School → Session Setup (all roles).',
      'Step 6 — Register Students: HCL SATHEE → Registration OR School → Sathee Registration.',
      'Step 7 — Submit Grievances if issues arise: Grievance in sidebar (all roles).',
      'Step 8 — Generate Reports: Reports in sidebar (scoped by role).',
      'Step 9 — Admin only: Manage users, RBAC, and system settings via God Mode.',
    ],
    note: 'Follow this order for correct setup. Labs must exist before Sessions. Equipment must be added before Allocation.',
  },
};

export const KEYWORD_MAP = [
  { keys: ['add school', 'create school', 'new school', 'school add', 'school banana', 'school kaise'], key: 'add school' },
  { keys: ['tag lab', 'lab tag', 'attach lab', 'link lab', 'add lab', 'lab school'], key: 'tag lab to school' },
  { keys: ['add equipment', 'new equipment', 'equipment add', 'equipment banana'], key: 'add equipment' },
  { keys: ['allocate equipment', 'equipment allocation', 'assign equipment', 'equipment assign'], key: 'equipment allocation' },
  { keys: ['session setup', 'create session', 'add session', 'new session', 'session create', 'session kaise', 'setup session', 'session banana'], key: 'session' },
  { keys: ['hcl sathee', 'hcl registration', 'hcl student', 'hcl section', 'hcl'], key: 'hcl sathee' },
  { keys: ['my entries', 'all entries', 'view session', 'see session', 'session list', 'session dekho', 'download image', 'session image', 'zip download', 'image download', 'sathee students', 'all schools', 'all labs', 'allocated equipment'], key: 'my entries' },
  { keys: ['sathee', 'register student', 'student registration', 'sathee registration'], key: 'sathee registration' },
  { keys: ['submit grievance', 'file grievance', 'add grievance', 'complaint', 'report issue', 'raise grievance', 'new grievance', 'shikayat'], key: 'grievance' },
  { keys: ['my grievance', 'view grievance', 'grievance status', 'check grievance', 'grievance list'], key: 'my grievances' },
  { keys: ['manage grievance', 'grievance manage', 'all grievance', 'admin grievance', 'god mode grievance'], key: 'manage grievances' },
  { keys: ['library', 'upload document', 'document upload', 'add url', 'artifact', 'upload file'], key: 'library' },
  { keys: ['pdf report', 'pdf download', 'download pdf'], key: 'pdf reports' },
  { keys: ['excel report', 'download report', 'excel', 'download excel'], key: 'excel reports' },
  { keys: ['dashboard stat', 'report stat', 'analytics', 'report chart', 'sessions chart'], key: 'dashboard stats' },
  { keys: ['report', 'generate report', 'reports kaise'], key: 'reports' },
  { keys: ['god mode', 'admin tools', 'god mod'], key: 'god mode' },
  { keys: ['add user', 'create user', 'new user', 'invite user', 'register user'], key: 'add user' },
  { keys: ['rbac', 'role based', 'feature access', 'toggle access', 'access control'], key: 'rbac' },
  { keys: ['system settings', 'upload limit', 'storage', 'file limit', 'document format'], key: 'system settings' },
  { keys: ['user activity', 'check activity', 'activity log', 'track user'], key: 'check user activity' },
  { keys: ['start', 'getting started', 'how to use', 'help me', 'guide', 'workflow', 'kya karu', 'process'], key: 'getting started' }
];
